import type { TreeData } from '../useTreeApi';
import { getMatchQuality, isFullMatch, MatchQuality } from './match-quality';
import {
  DIRECT_MATCH_BONUS,
  getBaseLeafScore,
  getBaseParentScore,
  getPositionBonusAnyOccurrence,
  getSuggestedMatchQuality,
  SUGGESTED_POSITION_MULTIPLIER,
} from './match-scoring';
import { resolveSuggestedTitles } from './normalizeSuggestedTitles';

enum FullMatch {
  SELF = 0,
  DESCENDANT = 1,
  NONE = 2,
}

type RankedNode = {
  node: TreeData;
  score: number;
  fullMatch: FullMatch;
  nameMatchDistance: number;
  nearestIsWordMatch: boolean;
};

const compareRanked = (a: RankedNode, b: RankedNode): number => {
  if (a.fullMatch !== b.fullMatch) return a.fullMatch - b.fullMatch;
  if (a.nameMatchDistance !== b.nameMatchDistance) {
    return a.nameMatchDistance - b.nameMatchDistance;
  }
  if (a.nearestIsWordMatch !== b.nearestIsWordMatch) {
    return a.nearestIsWordMatch ? -1 : 1;
  }
  return b.score - a.score;
};

type NodeType = 'leaf' | 'parent';

const getNodeScore = (node: TreeData, search: string): number => {
  const suggestedTitleStrings = resolveSuggestedTitles(node) ?? [];
  const directQuality = getMatchQuality(node.name, search);
  const suggestedQuality = getSuggestedMatchQuality(
    suggestedTitleStrings,
    search
  );

  const nodeType: NodeType = node.children?.length ? 'parent' : 'leaf';

  // Calculate base scores
  const directScore =
    nodeType === 'leaf'
      ? getBaseLeafScore('direct', directQuality)
      : getBaseParentScore('direct', directQuality);
  let suggestedScore = 0;
  if (directQuality === MatchQuality.NONE) {
    suggestedScore =
      nodeType === 'leaf'
        ? getBaseLeafScore('suggested', suggestedQuality)
        : getBaseParentScore('suggested', suggestedQuality);
  }

  let totalScore = directScore + suggestedScore;

  // Add massive bonus for any direct match to ensure they rank above all suggested matches
  if (directQuality !== MatchQuality.NONE) {
    totalScore += DIRECT_MATCH_BONUS;
  }

  // Add position bonuses
  if (directQuality !== MatchQuality.NONE) {
    totalScore += getPositionBonusAnyOccurrence(node.name, search);
  } else if (suggestedQuality !== MatchQuality.NONE) {
    const maxSuggestedBonus = Math.max(
      ...suggestedTitleStrings.map((title) =>
        getPositionBonusAnyOccurrence(title, search)
      )
    );
    totalScore += Math.floor(maxSuggestedBonus * SUGGESTED_POSITION_MULTIPLIER);
  }

  return totalScore;
};

export const sortTreeBySearchRelevance = (
  data: TreeData[],
  search: string
): TreeData[] => {
  if (!search?.trim()) return data;

  const rankNode = (node: TreeData): RankedNode => {
    const quality = getMatchQuality(node.name, search);
    const own: RankedNode = {
      node,
      score: getNodeScore(node, search),
      fullMatch: isFullMatch(node.name, search)
        ? FullMatch.SELF
        : FullMatch.NONE,
      nameMatchDistance:
        quality === MatchQuality.NONE ? Number.POSITIVE_INFINITY : 0,
      nearestIsWordMatch: quality === MatchQuality.EXACT,
    };

    if (!node.children?.length) {
      return own;
    }

    const children = node.children.map(rankNode).sort(compareRanked);
    const nearestChildDistance = children.reduce(
      (min, child) => Math.min(min, child.nameMatchDistance),
      Number.POSITIVE_INFINITY
    );
    const ownIsNearest = own.nameMatchDistance === 0;

    return {
      node: { ...node, children: children.map((child) => child.node) },
      score: own.score + children.reduce((sum, child) => sum + child.score, 0),
      fullMatch:
        own.fullMatch === FullMatch.SELF ||
        children[0]?.fullMatch === FullMatch.NONE
          ? own.fullMatch
          : FullMatch.DESCENDANT,
      nameMatchDistance: ownIsNearest ? 0 : nearestChildDistance + 1,
      nearestIsWordMatch: ownIsNearest
        ? own.nearestIsWordMatch
        : children.some(
            (child) =>
              child.nameMatchDistance === nearestChildDistance &&
              child.nearestIsWordMatch
          ),
    };
  };

  return data
    .map(rankNode)
    .sort(compareRanked)
    .map(({ node }) => node);
};

export const sortSelectedFirst = (
  data: TreeData[],
  selectedIds: string[],
  indeterminateIds: string[]
): TreeData[] => {
  const selected = new Set(selectedIds);
  const indeterminate = new Set(indeterminateIds);
  const selectedAncestors = new Set<string>();
  selectedIds.forEach((id) => {
    const segments = id.split('_');
    for (let depth = 1; depth < segments.length; depth += 1) {
      selectedAncestors.add(segments.slice(0, depth).join('_'));
    }
  });

  const sortLevel = (
    nodes: TreeData[]
  ): Array<{ node: TreeData; isMarked: boolean; index: number }> =>
    nodes
      .map((node, index) => {
        const sortedChildren = node.children
          ? sortLevel(node.children)
          : undefined;
        const isMarked =
          selected.has(node.id) ||
          selectedAncestors.has(node.id) ||
          indeterminate.has(node.id) ||
          Boolean(sortedChildren?.some((child) => child.isMarked));

        return {
          node: sortedChildren
            ? {
                ...node,
                children: sortedChildren.map((child) => child.node),
              }
            : node,
          isMarked,
          index,
        };
      })
      .sort((a, b) => {
        if (a.isMarked !== b.isMarked) return a.isMarked ? -1 : 1;

        const aIsOther = a.node.name === 'Other';
        const bIsOther = b.node.name === 'Other';
        if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;

        return a.index - b.index;
      });

  return sortLevel(data).map(({ node }) => node);
};
